import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSave,
  faTimes,
  faUser,
  faEnvelope,
  faLock,
  faUnlock,
  faBriefcase,
  faBullhorn,
  faStickyNote,
  faPlus,
  faSpinner,
  faExclamationTriangle,
  faEdit,
  faArchive,
  faCalendarAlt,
  faUserTag,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faChevronDown,
  faCheckCircle,
  faFilter,
  faClipboardCheck,
  faFileContract,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import ArchiveUserModal from '../../components/ArchiveUserModal';
import SuccessModal from '../../components/SuccessModal';

const ControllerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [controller, setController] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    specialization: '',
    status: 'active',
    notes: []
  });
  const [currentNote, setCurrentNote] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFormationsSection, setShowFormationsSection] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  
  // Checks history states
  const [checks, setChecks] = useState([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [checksCurrentPage, setChecksCurrentPage] = useState(1);
  const [checksTotalPages, setChecksTotalPages] = useState(1);
  const [checksTotal, setChecksTotal] = useState(0);
  const checksPerPage = 10;
  const [checksStatistics, setChecksStatistics] = useState({
    total: 0,
    validated: 0,
    rejected: 0,
    hold: 0,
    average_rating: 0
  });
  const [checksFilters, setChecksFilters] = useState({
    action: '',
    rating: '',
    from_date: '',
    to_date: ''
  });
  const [confidentialData, setConfidentialData] = useState(null);
  const [confidentialLoading, setConfidentialLoading] = useState(false);

  useEffect(() => {
    fetchController();
    fetchConfidentialData();
  }, [id]);

  const fetchConfidentialData = async () => {
    try {
      setConfidentialLoading(true);
      const response = await api.get(`/confidential-data/controller/${id}?masked=false`);
      if (response.data.success) {
        setConfidentialData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching confidential data:', error);
      setConfidentialData(null);
    } finally {
      setConfidentialLoading(false);
    }
  };

  useEffect(() => {
    if (controller && currentUser?.role === 'resp_controle') {
      fetchChecks();
    }
  }, [controller, checksCurrentPage, checksFilters, currentUser?.role]);

  const fetchController = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/controllers/${id}`);
      if (response.data.success) {
        const controllerData = response.data.data;
        setController(controllerData);
        setIsLocked(controllerData.is_locked || false);
        setFormData({
          name: controllerData.name || '',
          email: controllerData.email || '',
          specialization: controllerData.specialization || '',
          status: controllerData.status || 'active',
          notes: controllerData.notes || []
        });
      } else {
        setErrors({ fetch: 'Contrôleur non trouvé' });
      }
    } catch (error) {
      console.error('Error fetching controller:', error);
      setErrors({ fetch: 'Erreur lors du chargement du contrôleur' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleArrayAdd = (field, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUnlock = async () => {
    try {
      setUnlocking(true);
      setErrors({});
      setSuccessMessage('');
      
      await api.post(`/controllers/${id}/unlock-account`);
      setSuccessMessage('Compte déverrouillé avec succès');
      setShowSuccessModal(true);
      setIsLocked(false);
      setShowUnlockModal(false);
      fetchController(); // Refresh controller data
    } catch (error) {
      console.error('Error unlocking account:', error);
      if (error.response?.data?.message) {
        setErrors({ unlock: error.response.data.message });
      } else {
        setErrors({ unlock: 'Erreur lors du déverrouillage du compte' });
      }
    } finally {
      setUnlocking(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/controllers/${id}`, formData);
      if (response.data.success) {
        setController(response.data.data);
        setIsEditing(false);

        // Une suspension redistribue le travail en cours : le dire explicitement, sinon la
        // réaffectation se fait en silence.
        const redistribution = response.data.travail_redistribue;
        let message = 'Contrôleur mis à jour avec succès!';

        if (redistribution) {
          const parts = [];
          if (redistribution.reassignes) parts.push(`${redistribution.reassignes} affectation(s) réattribuée(s)`);
          if (redistribution.non_assignes) parts.push(`${redistribution.non_assignes} remise(s) dans le pool`);
          if (redistribution.lots_quittes) parts.push(`${redistribution.lots_quittes} lot(s) quitté(s)`);
          if (parts.length) message += ` ${parts.join(', ')}.`;
          if (redistribution.avertissements?.length) message += ` ${redistribution.avertissements.join(' ')}`;
        }

        setSuccessMessage(message);
        setShowSuccessModal(true);
      } else {
        setErrors({ submit: response.data.message || 'Erreur lors de la mise à jour' });
      }
    } catch (error) {
      console.error('Error updating controller:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Erreur lors de la mise à jour' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveClick = () => {
    setShowConfirmModal(true);
  };

  // L'appel et la saisie de la raison sont gérés par ArchiveUserModal ; on ne reçoit ici
  // que la réponse, pour annoncer la redistribution du travail avant de rediriger.
  const handleArchive = (data) => {
    let message = 'Contrôleur archivé avec succès!';

    const redistribution = data?.travail_redistribue;
    if (redistribution) {
      const parts = [];
      if (redistribution.reassignes) parts.push(`${redistribution.reassignes} affectation(s) réattribuée(s)`);
      if (redistribution.non_assignes) parts.push(`${redistribution.non_assignes} remise(s) dans le pool`);
      if (redistribution.lots_quittes) parts.push(`${redistribution.lots_quittes} lot(s) quitté(s)`);
      if (parts.length) message += ` ${parts.join(', ')}.`;
      if (redistribution.avertissements?.length) message += ` ${redistribution.avertissements.join(' ')}`;
    }

    setSuccessMessage(message);
    setShowSuccessModal(true);
    setTimeout(() => {
      navigate('/controllers');
    }, 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchChecks = async () => {
    if (!id) return;
    try {
      setChecksLoading(true);
      const params = {
        page: checksCurrentPage,
        per_page: checksPerPage,
        controller_id: id
      };
      if (checksFilters.action) params.action = checksFilters.action;
      if (checksFilters.rating) params.rating = checksFilters.rating;
      if (checksFilters.from_date) params.from_date = checksFilters.from_date;
      if (checksFilters.to_date) params.to_date = checksFilters.to_date;

      const response = await api.get('/control/checks', { params });
      setChecks(response.data.data || []);
      setChecksTotalPages(response.data.last_page || 1);
      setChecksTotal(response.data.total || response.data.data?.length || 0);
      
      // Set statistics from API response (global statistics, not just current page)
      if (response.data.statistics) {
        setChecksStatistics(response.data.statistics);
      } else {
        // Fallback: calculate from current page if statistics not provided
        setChecksStatistics({
          total: response.data.total || 0,
          validated: (response.data.data || []).filter(c => c.action === 'validate').length,
          rejected: (response.data.data || []).filter(c => c.action === 'reject').length,
          hold: (response.data.data || []).filter(c => c.action === 'hold').length,
          average_rating: (response.data.data || []).length > 0
            ? parseFloat(((response.data.data || []).reduce((sum, c) => sum + (c.rating || 0), 0) / (response.data.data || []).length).toFixed(1))
            : 0
        });
      }
    } catch (error) {
      console.error('Error fetching checks:', error);
    } finally {
      setChecksLoading(false);
    }
  };

  const handleChecksFilterChange = (field, value) => {
    setChecksFilters({ ...checksFilters, [field]: value });
    setChecksCurrentPage(1);
  };

  const handleChecksPageChange = (newPage) => {
    setChecksCurrentPage(newPage);
  };

  const getActionBadge = (action) => {
    const badges = {
      validate: { bg: 'bg-green-100', text: 'text-green-800', label: 'Validé' },
      reject: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeté' },
      hold: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' }
    };
    const badge = badges[action] || { bg: 'bg-gray-100', text: 'text-gray-800', label: action };
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du contrôleur...</p>
        </div>
      </div>
    );
  }

  if (errors.fetch) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="p-8">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
            <p className="text-gray-600 mb-4">{errors.fetch}</p>
            <button
              onClick={() => navigate('/controllers')}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retour aux contrôleurs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold text-gray-800">
              {isEditing ? 'MODIFICATION CONTRÔLEUR' : 'DÉTAILS CONTRÔLEUR'}
            </h1>
          </div>
          {!isEditing && (currentUser?.role === 'resp_controle' || currentUser?.role === 'admin_it') && (
            <div className="flex space-x-3">
              {currentUser?.role === 'admin_it' && (
                <button
                  onClick={() => setShowUnlockModal(true)}
                  disabled={!isLocked || unlocking}
                  className={`px-6 py-3 rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center disabled:opacity-50 ${
                    isLocked && !unlocking
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                  title={isLocked ? 'Déverrouiller le compte' : 'Le compte n\'est pas verrouillé'}
                >
                  {unlocking ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={isLocked ? faUnlock : faLock} className="mr-2" />
                  )}
                  {unlocking ? 'Déverrouillage...' : isLocked ? 'Déverrouiller' : 'Déverrouillé'}
                </button>
              )}
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-3 bg-gray-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faEdit} className="mr-2" />
                Modifier
              </button>
              <button
                onClick={handleArchiveClick}
                disabled={archiving}
                className="px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center disabled:opacity-50"
              >
                {archiving ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faArchive} className="mr-2" />
                )}
                Archiver
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6">
        {errors.submit && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {errors.submit}
            </div>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-8">
          {isEditing ? (
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-red-600" />
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Entrez le nom complet"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-red-600" />
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors bg-gray-100 cursor-not-allowed ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="exemple@email.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                {/* Specialization */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faBriefcase} className="mr-2 text-red-600" />
                    Spécialisation
                  </label>
                  <input
                    type="text"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                    placeholder="Ex: Qualité, Processus, etc."
                  />
                </div>

                {/* Statut */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUserTag} className="mr-2 text-red-600" />
                    Statut du compte
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                    <option value="suspended">Suspendu</option>
                  </select>
                  {formData.status === 'suspended' && controller?.status !== 'suspended' && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                      Un compte suspendu ne peut plus se connecter et est déconnecté immédiatement.
                      Ses affectations non terminées seront redistribuées aux autres contrôleurs des
                      enquêtes concernées : elles ne lui reviendront pas s'il est réactivé.
                      Son historique de contrôles et de présences est conservé.
                    </p>
                  )}
                  {formData.status === 'inactive' && (
                    <p className="text-xs text-gray-600 mt-2">
                      « Inactif » est un état de session : il repasse automatiquement à « Actif » à
                      la prochaine connexion du contrôleur.
                    </p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faStickyNote} className="mr-2 text-red-600" />
                  Notes
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={currentNote}
                    onChange={(e) => setCurrentNote(e.target.value)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                    placeholder="Ajouter une note"
                  />
                  <button
                    type="button"
                    onClick={() => handleArrayAdd('notes', currentNote, setCurrentNote)}
                    className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
                {formData.notes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.notes.map((note, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800"
                      >
                        {note}
                        <button
                          type="button"
                          onClick={() => handleArrayRemove('notes', index)}
                          className="ml-2 text-yellow-600 hover:text-yellow-800"
                        >
                          <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setErrors({});
                    fetchController(); // Reset form data
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {saving ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faUser} className="text-red-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{controller.name}</h3>
                    <p className="text-sm text-gray-500">Contrôleur</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                  <span className="text-gray-700">{controller.email}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                  {confidentialLoading ? (
                    <span className="text-gray-500">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Chargement...
                    </span>
                  ) : (
                    <span className="text-gray-700">
                      {confidentialData?.phone || <span className="text-gray-400 italic">Non renseigné</span>}
                    </span>
                  )}
                </div>
                {controller.specialization && (
                  <div className="flex items-center space-x-3">
                    <FontAwesomeIcon icon={faBriefcase} className="text-gray-400" />
                    <span className="text-gray-700">{controller.specialization}</span>
                  </div>
                )}
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" />
                  <span className="text-gray-700">
                    Créé le {formatDate(controller.created_at)}
                  </span>
                </div>
                {controller.created_by && (
                  <div className="flex items-center space-x-3">
                    <FontAwesomeIcon icon={faUserTag} className="text-gray-400" />
                    <span className="text-gray-700">Créé par {controller.created_by}</span>
                  </div>
                )}
                {/* Statut du compte */}
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faUserTag} className="text-gray-400" />
                  <span className="text-gray-700">
                    Statut:{' '}
                    {controller.status === 'active' ? (
                      <span className="text-green-600 font-medium">Actif</span>
                    ) : controller.status === 'suspended' ? (
                      <span className="text-red-600 font-medium">Suspendu</span>
                    ) : (
                      <span className="text-gray-500 font-medium">Inactif</span>
                    )}
                  </span>
                </div>
                {/* Verification Status */}
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faClipboardCheck} className="text-gray-400" />
                  <div>
                    <span className="text-gray-700">
                      Vérification: {controller.verified === 'yes' ? (
                        <span className="text-green-600 font-medium">Vérifié</span>
                      ) : controller.verified === 'partially' ? (
                        <span className="text-yellow-600 font-medium">Partiellement</span>
                      ) : (
                        <span className="text-red-600 font-medium">Non vérifié</span>
                      )}
                    </span>
                  </div>
                </div>
                {/* Contract Information */}
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFileContract} className="text-gray-400" />
                  <div>
                    {controller.contrat_signed ? (
                      <div>
                        <span className="text-gray-700 font-medium text-green-600">Contrat signé</span>
                        {controller.contract_start_date && (
                          <p className="text-xs text-gray-500">
                            Début: {new Date(controller.contract_start_date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                        {controller.contract_end_date && (
                          <p className="text-xs text-gray-500">
                            Fin: {new Date(controller.contract_end_date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">Contrat non signé</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              {controller.notes && controller.notes.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    <FontAwesomeIcon icon={faStickyNote} className="mr-2 text-red-600" />
                    Notes
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {controller.notes.map((note, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800"
                      >
                        {note}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Formations - Collapsible */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 mt-6">
                <button
                  onClick={() => setShowFormationsSection(!showFormationsSection)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                >
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faBullhorn} className="mr-2 text-red-600" />
                    Formations Complétées
                  </h2>
                  <FontAwesomeIcon 
                    icon={showFormationsSection ? faChevronUp : faChevronDown} 
                    className="text-gray-500 transition-transform duration-200"
                  />
                </button>
                
                {showFormationsSection && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <div className="mt-4">
                      {controller.completed_formations && controller.completed_formations.length > 0 ? (
                        <div className="space-y-3">
                          {controller.completed_formations.map((formation) => (
                            <div key={formation.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                                    {formation.title}
                                  </h3>
                                  {formation.description && (
                                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                      {formation.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-4 text-xs text-gray-500">
                                    <div className="flex items-center">
                                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                                      <span>
                                        Complétée le {new Date(formation.completed_at).toLocaleDateString('fr-FR', { 
                                          year: 'numeric', 
                                          month: 'long', 
                                          day: 'numeric' 
                                        })}
                                      </span>
                                    </div>
                                    <div className="flex items-center">
                                      <FontAwesomeIcon 
                                        icon={formation.completed_method === 'online' ? faCheckCircle : faClipboardCheck} 
                                        className={`mr-1 ${formation.completed_method === 'online' ? 'text-green-500' : 'text-blue-500'}`}
                                      />
                                      <span className="capitalize">
                                        {formation.completed_method === 'online' ? 'En ligne' : 'Présence marquée'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <FontAwesomeIcon icon={faBullhorn} className="text-4xl text-gray-300 mb-2" />
                          <p>Aucune formation complétée</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Checks History - Only for resp_controle - Separate Card */}
        {currentUser?.role === 'resp_controle' && !isEditing && (
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-8 mt-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Historique des Contrôles
            </h2>

                  {/* Stats Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg shadow p-4">
                      <div className="text-sm font-medium text-gray-500 uppercase">Total</div>
                      <div className="mt-2 text-2xl font-bold text-gray-800">{checksStatistics.total || checksTotal}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg shadow p-4">
                      <div className="text-sm font-medium text-gray-500 uppercase">Validés</div>
                      <div className="mt-2 text-2xl font-bold text-green-600">
                        {checksStatistics.validated || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg shadow p-4">
                      <div className="text-sm font-medium text-gray-500 uppercase">Rejetés</div>
                      <div className="mt-2 text-2xl font-bold text-red-600">
                        {checksStatistics.rejected || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg shadow p-4">
                      <div className="text-sm font-medium text-gray-500 uppercase">Note Moyenne</div>
                      <div className="mt-2 text-2xl font-bold text-red-600">
                        {checksStatistics.average_rating || 0}/5
                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-center">
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
                        <span className="font-medium text-gray-700">Filtres:</span>
                      </div>
                      
                      <select
                        value={checksFilters.action}
                        onChange={(e) => handleChecksFilterChange('action', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                      >
                        <option value="">Toutes les actions</option>
                        <option value="validate">Validé</option>
                        <option value="reject">Rejeté</option>
                        <option value="hold">En attente</option>
                      </select>

                      <select
                        value={checksFilters.rating}
                        onChange={(e) => handleChecksFilterChange('rating', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                      >
                        <option value="">Toutes les notes</option>
                        <option value="5">5 étoiles</option>
                        <option value="4">4 étoiles</option>
                        <option value="3">3 étoiles</option>
                        <option value="2">2 étoiles</option>
                        <option value="1">1 étoile</option>
                      </select>

                      <input
                        type="date"
                        value={checksFilters.from_date}
                        onChange={(e) => handleChecksFilterChange('from_date', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                        placeholder="Date début"
                      />

                      <input
                        type="date"
                        value={checksFilters.to_date}
                        onChange={(e) => handleChecksFilterChange('to_date', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                        placeholder="Date fin"
                      />
                    </div>
                  </div>

                  {/* Checks Table */}
                  {checksLoading ? (
                    <div className="text-center py-8">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-red-600" />
                      <p className="mt-4 text-gray-600">Chargement de l'historique...</p>
                    </div>
                  ) : (
                    <>
                      <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Date/Heure
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Enquêteur
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  ID Enquête
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  ID de la réponse
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Tel de client
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Note
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Action
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Correction
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Commentaire
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {checks.map((check) => (
                                <tr key={check.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {new Date(check.checked_at).toLocaleString('fr-FR')}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {check.assignment?.interviewer?.name || check.assignment?.supervisor?.name || check.f2f_submission?.person?.name || 'N/A'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {check.assignment?.interviewer?.ref || check.assignment?.supervisor?.ref || check.f2f_submission?.person?.ref || ''}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {(() => {
                                      const survey = check.assignment?.survey || check.submission?.survey || check.f2f_submission?.survey;
                                      return survey ? (
                                        <div>
                                          <div className="font-medium">{survey.title || 'N/A'}</div>
                                          <div className="text-xs text-gray-500">ID: {survey.limesurvey_id || 'N/A'}</div>
                                        </div>
                                      ) : 'N/A';
                                    })()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {check.assignment?.submission?.response_id || check.assignment?.f2f_submission?.response_id || check.submission?.response_id || check.f2f_submission?.response_id || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {check.tel_value || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <span className="text-yellow-400">{'★'.repeat(check.rating)}</span>
                                      <span className="text-gray-300">{'★'.repeat(5 - check.rating)}</span>
                                      <span className="ml-2 text-sm text-gray-600">{check.rating}/5</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {getActionBadge(check.action)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {check.correction_applied ? (
                                      <span className="text-orange-600">Oui</span>
                                    ) : (
                                      <span className="text-gray-400">Non</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                    {check.comment || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          {checks.length === 0 && (
                            <div className="text-center py-12 text-gray-500">
                              Aucun contrôle trouvé
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Pagination */}
                      {!checksLoading && checksTotalPages > 1 && (
                        <div className="mt-6 flex items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
                          <div className="flex items-center text-sm text-gray-700">
                            <span>
                              Affichage de {((checksCurrentPage - 1) * checksPerPage) + 1} à {Math.min(checksCurrentPage * checksPerPage, checksTotal)} sur {checksTotal} contrôles
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleChecksPageChange(checksCurrentPage - 1)}
                              disabled={checksCurrentPage === 1}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                            </button>
                            
                            <div className="flex space-x-1">
                              {Array.from({ length: Math.min(5, checksTotalPages) }, (_, i) => {
                                let pageNum;
                                if (checksTotalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (checksCurrentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (checksCurrentPage >= checksTotalPages - 2) {
                                  pageNum = checksTotalPages - 4 + i;
                                } else {
                                  pageNum = checksCurrentPage - 2 + i;
                                }
                                
                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => handleChecksPageChange(pageNum)}
                                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                      checksCurrentPage === pageNum
                                        ? 'bg-red-600 text-white'
                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                            </div>
                            
                            <button
                              onClick={() => handleChecksPageChange(checksCurrentPage + 1)}
                              disabled={checksCurrentPage === checksTotalPages}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && controller && (
        <ArchiveUserModal
          user={controller}
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onArchive={handleArchive}
          archiveEndpoint={`/controllers/${id}/archive`}
          archiveMethod="POST"
          title="Archiver le contrôleur"
          subtitle="Cette action enverra le contrôleur vers les archives"
          entityLabel="contrôleur"
          consequences={[
            'Son compte sera désactivé : il ne pourra plus se connecter.',
            'Son historique est conservé (contrôles, présences, paiements).',
            'Ses affectations non terminées seront redistribuées aux autres contrôleurs.',
            'Vous pourrez le restaurer à tout moment depuis la page des archives.',
          ]}
        />
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Opération terminée"
        message={successMessage}
      />
      
      {/* Unlock Modal */}
      <ConfirmationModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onConfirm={handleUnlock}
        title="Déverrouiller le compte"
        message="Êtes-vous sûr de vouloir déverrouiller ce compte ? Les tentatives de connexion échouées seront réinitialisées."
        confirmText="Déverrouiller"
        cancelText="Annuler"
        confirmColor="bg-orange-600 hover:bg-orange-700"
        loading={unlocking}
      />
    </div>
  );
};

export default ControllerDetail;
